/**
 * FT-1484 step 3: THE LIVE ROLE OVERLAY — every library Lot's latest
 * version, laid over the script that uses it, on the storyteller's client;
 * the town takes the storyteller's. A port of botc's golem/houseRoles.js
 * (read its header for the whole model); L.O.T.S. differences:
 *
 *   - the officials carry a Shadow side (`shadowAbility`), overlaid too;
 *   - there is no house STEPS layer in this port's night schema (nightInfo
 *     has no registerHouseNight), so an official's steps are not overlaid —
 *     its words, night-sheet reminders and spent token are;
 *   - the house overlay carries no borrowed icon here (the route doesn't
 *     serve one), and there is no owner's pen on the officials yet, so none
 *     of botc's ownership probe is ported.
 *
 * FT-1485: THE LIBRARY IS THE OFFICIALS' ONE HOME. OFFICIAL Lots follow
 * `GET /official` — every official row, edited or not, read in full once per
 * page load and then only "saved since" — by `officialId`, written onto the
 * bundled objects in place (name, both abilities, team, reminders, setup,
 * whether it wakes, the borrowed art, the night); roles.json is only the
 * fallback when that can't be reached and the provider of the fields no row
 * stores (the edition, the night-order position). CUSTOM Lots follow
 * `GET /batch?ids=` by their library id (`golemRoleId`), laid onto the loaded
 * script by the store's `liveRolesApplied`. The storyteller refreshes on
 * page load, whenever a script lands and at Play again — NEVER while its game
 * is dealt; players take the storyteller's through the edition frame.
 */
import Vue from "vue";
import rolesJSON from "../roles.json";
// the house's night WORDS land on the copy layer's house map
import { registerHouseCopy } from "./nightSigns";
// a library row → the script's custom-role shape
import { liveFieldsDiffer, teamFromRoleType, toAppRole } from "./roles";
// FT-1546: the card art's one reader (only the platform's own addresses)
import { cardArtOf } from "./cardArt";
// the host's per-town deal stash — half of the lock
import { dealTimeFor } from "./stats";

const API = "/api/lots/roles";

/** The batch read's own cap (routes.ts ROLE_BATCH_MAX). */
const BATCH_MAX = 100;

/** The bundled officials by id — the SAME objects the store maps. */
const base = new Map(rolesJSON.map((role) => [role.id, role]));

/** FT-1485: the teams a row may give an official (app spelling). */
const TEAMS = new Set(["townsfolk", "outsider", "minion", "demon", "traveler"]);

/** FT-1485: the art each official wears as bundled — what a row whose `icon`
 *  names its own art (or none) leaves it wearing. */
const baseAlt = new Map(rolesJSON.map((role) => [role.id, role.imageAlt]));

/** Each official's own text as shipped — what a Lot goes back to when the
 *  overlay stops covering it. Taken at import. */
const stock = new Map(
  rolesJSON.map((role) => [
    role.id,
    {
      name: role.name,
      ability: role.ability,
      shadowAbility: role.shadowAbility,
      reminders: Array.isArray(role.reminders) ? role.reminders.slice() : [],
      setup: role.setup,
      firstNightReminder: role.firstNightReminder,
      otherNightReminder: role.otherNightReminder,
      // FT-1485: the team and the night positions are the rows' now too
      team: role.team,
      firstNight: role.firstNight,
      otherNight: role.otherNight,
    },
  ]),
);

/** One reminder key onto the bundled object — the house's text where it
 *  wrote one, the shipped text otherwise. */
function setReminder(role, key, text, shipped) {
  if (text) Vue.set(role, key, text);
  else if (shipped !== undefined) Vue.set(role, key, shipped);
  else if (key in role) Vue.delete(role, key);
}

// ─── THE OFFICIALS ───────────────────────────────────────────────────────────

/** Lay one override onto its bundled Lot. */
function overlayOne(o) {
  const role = o && base.get(o.officialId);
  if (!role) return false;
  if (typeof o.name === "string" && o.name.trim()) role.name = o.name.trim();
  if (typeof o.ability === "string" && o.ability.trim()) {
    role.ability = o.ability.trim();
  }
  if (typeof o.shadowAbility === "string" && o.shadowAbility.trim()) {
    Vue.set(role, "shadowAbility", o.shadowAbility.trim());
  }
  if (Array.isArray(o.reminders)) role.reminders = o.reminders.slice();
  if (typeof o.setup === "boolean") role.setup = o.setup;
  // FT-1485: the TEAM is the row's (houseEntry spelled it the app's way)
  if (TEAMS.has(o.team)) role.team = o.team;
  // FT-1485: WHETHER it wakes is the row's; WHERE is the script's night
  // order, so the shipped position stands. A row that says it sleeps takes
  // it off that night. Always over a reset Lot.
  if (o.wakesFirst === false) role.firstNight = 0;
  if (o.wakesOther === false) role.otherNight = 0;
  // FT-1485: the ART the row borrows — `icon` names the official whose
  // engraving this Lot wears; its own id (or none) means its own art. Only a
  // bundled id lands (Token `require`s `imageAlt`).
  if (typeof o.icon === "string") {
    const icon = o.icon.trim();
    const alt = baseAlt.get(o.officialId);
    if (icon && icon !== o.officialId && base.has(icon)) {
      Vue.set(role, "imageAlt", icon);
    } else if (alt) {
      Vue.set(role, "imageAlt", alt);
    } else if ("imageAlt" in role) {
      Vue.delete(role, "imageAlt");
    }
  }
  if (o.night && typeof o.night === "object" && !Array.isArray(o.night)) {
    registerHouseCopy(o.officialId, o.night.copy);
    const rem = o.night.reminders || {};
    const shipped = stock.get(o.officialId) || {};
    setReminder(
      role,
      "firstNightReminder",
      rem.first,
      shipped.firstNightReminder,
    );
    setReminder(
      role,
      "otherNightReminder",
      rem.other,
      shipped.otherNightReminder,
    );
    Vue.set(role, "golemSpentToken", o.night.spentToken === true);
  }
  // FT-1491: the character's SECTIONS — the row's whole list onto the
  // bundled object as golemSections (the key every reader takes); none
  // clears. Official is just a tag: the same key a custom character carries.
  if (Array.isArray(o.sections)) {
    if (o.sections.length) {
      Vue.set(
        role,
        "golemSections",
        o.sections.map((s) => ({ ...s })),
      );
    } else if ("golemSections" in role) {
      Vue.delete(role, "golemSections");
    }
  }
  // FT-1546: the Lot's CARD ART — the row's onto the bundled object as
  // golemCardArt (the key every card reads); none clears.
  const cardArt = cardArtOf(o.cardArt);
  if (cardArt) Vue.set(role, "golemCardArt", cardArt);
  else if ("golemCardArt" in role) Vue.delete(role, "golemCardArt");
  return true;
}

/** One official back to exactly its shipped self. */
function resetOne(officialId) {
  const role = base.get(officialId);
  const shipped = stock.get(officialId);
  if (!role || !shipped) return;
  role.name = shipped.name;
  role.ability = shipped.ability;
  if (shipped.shadowAbility === undefined) Vue.delete(role, "shadowAbility");
  else Vue.set(role, "shadowAbility", shipped.shadowAbility);
  role.reminders = shipped.reminders.slice();
  role.team = shipped.team;
  role.firstNight = shipped.firstNight;
  role.otherNight = shipped.otherNight;
  const alt = baseAlt.get(officialId);
  if (alt) Vue.set(role, "imageAlt", alt);
  else if ("imageAlt" in role) Vue.delete(role, "imageAlt");
  if (shipped.setup === undefined) Vue.delete(role, "setup");
  else role.setup = shipped.setup;
  setReminder(role, "firstNightReminder", "", shipped.firstNightReminder);
  setReminder(role, "otherNightReminder", "", shipped.otherNightReminder);
  if ("golemSpentToken" in role) Vue.delete(role, "golemSpentToken");
  // FT-1491: roles.json ships no sections
  if ("golemSections" in role) Vue.delete(role, "golemSections");
  // FT-1546: roles.json ships no card art
  if ("golemCardArt" in role) Vue.delete(role, "golemCardArt");
  registerHouseCopy(officialId, null);
}

/** An override reduced to what the overlay reads — the frame's shape.
 *  FT-1485: a library row speaks `roleType` ("traveller") and night
 *  positions (a L.O.T.S. row has no wake flags: a position > 0 wakes); an
 *  entry already in this shape (the edition frame) speaks `team` and the
 *  flags. */
function houseEntry(o) {
  const wakes = (flag, position) =>
    typeof flag === "boolean"
      ? flag
      : typeof position === "number"
      ? position > 0
      : undefined;
  return {
    officialId: o.officialId,
    name: o.name,
    ability: o.ability,
    shadowAbility: o.shadowAbility,
    team: o.team || (o.roleType ? teamFromRoleType(o.roleType) : undefined),
    reminders: o.reminders,
    setup: o.setup,
    wakesFirst: wakes(o.wakesFirst, o.firstNight),
    wakesOther: wakes(o.wakesOther, o.otherNight),
    icon: o.icon,
    night: o.night === undefined ? null : o.night,
    // FT-1491: the row's sections (the frame strips the storyteller's)
    sections: Array.isArray(o.sections) ? o.sections : undefined,
    // FT-1546: the row's card art, or null
    cardArt: cardArtOf(o.cardArt),
    version: o.version,
  };
}

/**
 * FT-1485: does this entry make its Lot differ from roles.json as shipped?
 * Every official has an entry now; a player's client starts from roles.json,
 * so the edition frame need only carry the ones that differ (see botc).
 */
function differsFromStock(o) {
  const s = stock.get(o.officialId);
  if (!s) return false;
  // FT-1491: roles.json ships no sections, so any section differs
  if (Array.isArray(o.sections) && o.sections.length) return true;
  // FT-1546: roles.json ships no card art, so any card art differs
  if (cardArtOf(o.cardArt)) return true;
  const text = (v) => (typeof v === "string" ? v.trim() : "");
  if (text(o.name) && text(o.name) !== s.name) return true;
  if (text(o.ability) && text(o.ability) !== s.ability) return true;
  if (
    text(o.shadowAbility) &&
    text(o.shadowAbility) !== text(s.shadowAbility)
  ) {
    return true;
  }
  if (TEAMS.has(o.team) && o.team !== s.team) return true;
  if (
    Array.isArray(o.reminders) &&
    JSON.stringify(o.reminders) !== JSON.stringify(s.reminders)
  ) {
    return true;
  }
  if (typeof o.setup === "boolean" && o.setup !== !!s.setup) return true;
  if (o.wakesFirst === false && s.firstNight > 0) return true;
  if (o.wakesOther === false && s.otherNight > 0) return true;
  const icon = text(o.icon);
  if (icon && icon !== o.officialId && base.has(icon)) return true;
  const n = o.night;
  if (n && typeof n === "object") {
    if (n.copy && Object.keys(n.copy).length) return true;
    if (n.spentToken === true) return true;
    const rem = n.reminders || {};
    if (text(rem.first) && text(rem.first) !== text(s.firstNightReminder)) {
      return true;
    }
    if (text(rem.other) && text(rem.other) !== text(s.otherNightReminder)) {
      return true;
    }
  }
  return false;
}

/** officialId → the house entry standing on this client right now. */
const applied = new Map();

/** The standing overlay's fingerprint, so an identical refresh is a no-op. */
function signature(map) {
  return JSON.stringify(
    [...map.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  );
}

/** REPLACE the standing house overlay with `list` (see botc). Returns
 *  whether anything moved. */
function setHouse(list, store) {
  const next = new Map();
  (Array.isArray(list) ? list : []).forEach((o) => {
    if (o && base.has(o.officialId)) next.set(o.officialId, houseEntry(o));
  });
  if (signature(next) === signature(applied)) return false;
  new Set([...applied.keys(), ...next.keys()]).forEach(resetOne);
  applied.clear();
  next.forEach((entry, id) => {
    overlayOne(entry);
    applied.set(id, entry);
  });
  if (store) store.commit("houseRolesApplied");
  return true;
}

/** THE STORYTELLER'S HOUSE, for the edition frame — narrowed to the Lots
 *  this game can reach (the script's, and the travellers), and (FT-1485) to
 *  the ones that differ from roles.json as shipped. */
export function houseFrame(state) {
  return [...applied.values()].filter(
    (o) =>
      ((state.roles && state.roles.has(o.officialId)) ||
        (state.otherTravelers && state.otherTravelers.has(o.officialId))) &&
      differsFromStock(o),
  );
}

/** A PLAYER's side of the frame: the storyteller's house replaces ours (a
 *  player never fetches its own — `mayRefresh` refuses spectators). */
export function applyHouseFrame(list, store) {
  setHouse(list, store);
}

// ─── THE CUSTOM LOTS ─────────────────────────────────────────────────────────

/** The distinct library ids of the script's custom Lots (Fabled included). */
function scriptLibraryIds(state) {
  const ids = new Set();
  const add = (role) => {
    if (role && role.isCustom && role.golemRoleId) ids.add(role.golemRoleId);
  };
  state.roles.forEach(add);
  state.fabled.forEach(add);
  return [...ids];
}

/** The latest rows for `ids`, BATCH_MAX a request. Throws on a failure. */
export async function fetchLatestRoles(ids) {
  const out = [];
  for (let i = 0; i < ids.length; i += BATCH_MAX) {
    const chunk = ids.slice(i, i + BATCH_MAX).map(encodeURIComponent);
    const res = await fetch(`${API}/batch?ids=${chunk.join(",")}`, {
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(`batch failed (${res.status})`);
    const { roles } = await res.json();
    if (Array.isArray(roles)) out.push(...roles);
  }
  return out;
}

/** Library rows onto the loaded script, by library id — only the Lots whose
 *  library fields actually moved are committed. Returns how many. */
export function applyLibraryRows(list, store) {
  const loaded = new Map();
  const collect = (role) => {
    if (role && role.isCustom && role.golemRoleId) {
      loaded.set(role.golemRoleId, role);
    }
  };
  store.state.roles.forEach(collect);
  store.state.fabled.forEach(collect);
  const fresh = new Map();
  (Array.isArray(list) ? list : []).forEach((row) => {
    const role = row && loaded.get(row.id);
    if (!role) return;
    const next = toAppRole(row);
    if (liveFieldsDiffer(role, next)) fresh.set(row.id, next);
  });
  if (fresh.size) store.commit("liveRolesApplied", fresh);
  return fresh.size;
}

// ─── THE REFRESH, AND THE LOCK ───────────────────────────────────────────────

/** socket.js `_isDealt`, read from the store. */
function isDealt(state) {
  const { session } = state;
  return !!session.isRolesDistributed || !!dealTimeFor(session.sessionId || "");
}

/** The storyteller, between games (`boot` = a page load, allowed). */
function mayRefresh(state, boot) {
  if (state.session.isSpectator) return false;
  return boot || !isDealt(state);
}

// ─── THE OFFICIALS' HOME (FT-1485, see botc) ─────────────────────────────────

/** officialId → the library row that IS that Lot, as last read. */
const officialRows = new Map();

/** Which library rows ARE official Lots — the script builder leaves them out
 *  of its library lists. Reactive. */
export const officials = Vue.observable({ rowIds: [] });

/** FT-1486: the library row that IS official `officialId`, from the one
 *  officials read already made — no request — or null before it lands. */
export function cachedOfficialRow(officialId) {
  return officialRows.get(officialId) || null;
}

/** The newest save time read so far, and whether this page load has had its
 *  one full read (a failed one is not counted). */
let watermark = "";
let fullyRead = false;

/** The first read of a page load takes every official row; every later one
 *  only the rows saved since the newest seen, merged over what it holds.
 *  Resolves to the whole standing list; throws on a failed request. */
async function fetchOfficials() {
  const since =
    fullyRead && watermark ? `?since=${encodeURIComponent(watermark)}` : "";
  const res = await fetch(`${API}/official${since}`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`officials failed (${res.status})`);
  const { roles } = await res.json();
  (Array.isArray(roles) ? roles : []).forEach((row) => {
    if (!row || !row.officialId) return;
    officialRows.set(row.officialId, row);
    if (typeof row.updatedAt === "string" && row.updatedAt > watermark) {
      watermark = row.updatedAt;
    }
  });
  fullyRead = true;
  const ids = [...officialRows.values()].map((row) => row.id);
  if (ids.join() !== officials.rowIds.join()) officials.rowIds = ids;
  return [...officialRows.values()];
}

/** Take every library Lot this script uses at its latest, unless the game
 *  is dealt — checked before asking and again when the answers land. */
export async function refreshLiveRoles(store, { boot = false } = {}) {
  if (!mayRefresh(store.state, boot)) return false;
  const ids = scriptLibraryIds(store.state);
  const [officialList, libraryRows] = await Promise.all([
    fetchOfficials().catch(() => null),
    ids.length ? fetchLatestRoles(ids).catch(() => null) : [],
  ]);
  if (!mayRefresh(store.state, boot)) return false;
  // FT-1485: an unreachable library keeps what stands (roles.json as shipped)
  const houseMoved = officialList ? setHouse(officialList, store) : false;
  const customs = libraryRows ? applyLibraryRows(libraryRows, store) : 0;
  return houseMoved || customs > 0;
}

let timer = null;
let running = null;
let again = false;

function run(store, opts) {
  if (running) {
    again = true;
    return running;
  }
  running = refreshLiveRoles(store, opts)
    .catch(() => false)
    .finally(() => {
      running = null;
      if (again) {
        again = false;
        run(store);
      }
    });
  return running;
}

function schedule(store) {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    run(store);
  }, 0);
}

const TRIGGERS = new Set([
  "setCustomRoles",
  "setEdition",
  "clearEnded",
  "session/setSessionId",
]);

/** Wire the live overlay to the store (see botc's bindLiveRoles). */
export function bindLiveRoles(store) {
  store.subscribe(({ type, payload }) => {
    if (
      TRIGGERS.has(type) ||
      (type === "toggleModal" && payload === "edition")
    ) {
      schedule(store);
    }
  });
  if (!store.state.session.isSpectator) run(store, { boot: true });
}
